import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../../../core/services/api.service';
import {
  CreateGroupPayload,
  CreatePagesResponse,
  DeckGroup,
  DeckProductContext,
  DeckSlide,
  GroupListResponse,
  PageListResponse,
  PageSpec,
  PatchGroupPayload,
  PatchSlidePayload,
} from './deck-admin.models';

// Models live in deck-admin.models.ts; re-export so existing imports keep working.
export * from './deck-admin.models';

/**
 * HTTP client for the CRM Node backend's /deck-admin routes
 * (prism-backend-nodejs → live-call-agent module), which replaced the Python
 * live-call service's /api/deck-admin endpoints.
 *
 * The Node API scopes every route by a `:product` path segment — a product pid
 * or keyname (e.g. `credee`). The page resolves the globally selected product
 * into a {@link DeckProductContext} via {@link setProductContext}; every call
 * then hits `/deck-admin/<product>/…` on the main CRM API (`environment.apiUrl`),
 * authenticated like any other CRM request.
 */
@Injectable({
  providedIn: 'root',
})
export class DeckAdminService {
  private api = inject(ApiService);

  /** Product scope for every request; empty until the page resolves a product. */
  private productContext: DeckProductContext = { productKey: null, productPid: null };

  /** Suppress ApiService's global toaster/loader — the page renders its own states. */
  private readonly quiet = { showErrorToaster: false, showLoader: false } as const;

  // ─── Product scope ────────────────────────────────────────────────────────

  /** Set which product's deck subsequent calls address. */
  setProductContext(context: DeckProductContext): void {
    this.productContext = context;
  }

  getProductContext(): DeckProductContext {
    return this.productContext;
  }

  /** The `:product` path segment — keyname preferred, pid as fallback. */
  private productSegment(): string | null {
    const { productKey, productPid } = this.productContext;
    if (productKey) return productKey;
    if (productPid != null) return String(productPid);
    return null;
  }

  /** Route prefix for the selected product, or null when no product is set. */
  private basePath(): string | null {
    const segment = this.productSegment();
    return segment ? `/deck-admin/${encodeURIComponent(segment)}` : null;
  }

  private noProduct(): Observable<never> {
    return throwError(() => ({
      message: 'No product selected — pick a product from the sidebar first.',
    }));
  }

  // ─── Categories ──────────────────────────────────────────────────────────

  /** GET /deck-admin/:product/groups — list categories with their page counts */
  listGroups(includeInactive = true): Observable<GroupListResponse> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    const params: Record<string, string | number> = {};
    if (!includeInactive) params['includeInactive'] = 0;
    return this.api
      .get<GroupListResponse>(`${base}/groups`, params, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** POST /deck-admin/:product/groups — 409 if the key is taken (soft-deleted included) */
  createGroup(payload: CreateGroupPayload): Observable<{ group: DeckGroup }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .post<{ group: DeckGroup }>(`${base}/groups`, payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** PATCH /deck-admin/:product/groups/:groupKey — is_intro clears the previous opener */
  patchGroup(groupKey: string, payload: PatchGroupPayload): Observable<{ group: DeckGroup }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .patch<{ group: DeckGroup }>(
        `${base}/groups/${encodeURIComponent(groupKey)}`,
        payload,
        this.quiet
      )
      .pipe(catchError(this.handleError));
  }

  /** DELETE /deck-admin/:product/groups/:groupKey — soft-deletes the category AND its pages */
  deleteGroup(groupKey: string): Observable<{ deleted: boolean }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .delete<{ deleted: boolean }>(`${base}/groups/${encodeURIComponent(groupKey)}`, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** GET /deck-admin/industries — canonical industry keys (product-agnostic). */
  listIndustries(): Observable<string[]> {
    return this.api
      .get<{ industries: string[] }>('/deck-admin/industries', {}, this.quiet)
      .pipe(
        map((r) => r?.industries ?? []),
        catchError(this.handleError)
      );
  }

  // ─── Pages inside a category ─────────────────────────────────────────────

  /** GET /deck-admin/:product/groups/:groupKey/slides */
  listPages(groupKey: string): Observable<PageListResponse> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .get<PageListResponse>(`${base}/groups/${encodeURIComponent(groupKey)}/slides`, {}, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** GET /deck-admin/:product/groups/:groupKey/slides/:pageKey */
  getPage(groupKey: string, pageKey: string, includeHtml = false): Observable<{ slide: DeckSlide }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    const params: Record<string, string | number> = {};
    if (includeHtml) params['includeHtml'] = 1;
    return this.api
      .get<{ slide: DeckSlide }>(
        `${base}/groups/${encodeURIComponent(groupKey)}/slides/${encodeURIComponent(pageKey)}`,
        params,
        this.quiet
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * POST /deck-admin/:product/groups/:groupKey/slides — add one or many pages.
   *
   * ALL-OR-NOTHING. `files` is index-aligned with `specs`; a duplicate page_key on the
   * 4th file returns 409 and writes none of them. Pass `overwrite` to deliberately
   * replace existing pages (bumps their version).
   *
   * Multipart goes through ApiService like every other request, so it gets the live
   * access token AND the refresh-on-401 retry. This upload used to be a raw axios call
   * with a hand-built token — every upload after the short-lived token expired died with
   * 401 "Token has expired" because nothing refreshed it.
   */
  createPages(
    groupKey: string,
    specs: PageSpec[],
    files: File[],
    opts: { uploadedBy?: string; overwrite?: boolean } = {}
  ): Observable<CreatePagesResponse> {
    const base = this.basePath();
    if (!base) return this.noProduct();

    const fd = new FormData();
    fd.append('pages', JSON.stringify(specs));
    for (const file of files) fd.append('files', file, file.name);
    if (opts.uploadedBy) fd.append('uploaded_by', opts.uploadedBy);
    if (opts.overwrite) fd.append('overwrite', 'true');

    return this.api
      .post<CreatePagesResponse>(`${base}/groups/${encodeURIComponent(groupKey)}/slides`, fd, {
        ...this.quiet,
        // A batch upload carries N HTML documents; the default 60s was already tight.
        timeout: 120_000,
      })
      .pipe(catchError(this.handleError));
  }

  /** PATCH /deck-admin/:product/groups/:groupKey/slides/:pageKey — metadata only */
  patchPage(
    groupKey: string,
    pageKey: string,
    payload: PatchSlidePayload
  ): Observable<{ slide: DeckSlide }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .patch<{ slide: DeckSlide }>(
        `${base}/groups/${encodeURIComponent(groupKey)}/slides/${encodeURIComponent(pageKey)}`,
        payload,
        this.quiet
      )
      .pipe(catchError(this.handleError));
  }

  /** DELETE /deck-admin/:product/groups/:groupKey/slides/:pageKey — soft delete */
  deletePage(groupKey: string, pageKey: string): Observable<{ deleted: boolean }> {
    const base = this.basePath();
    if (!base) return this.noProduct();
    return this.api
      .delete<{ deleted: boolean }>(
        `${base}/groups/${encodeURIComponent(groupKey)}/slides/${encodeURIComponent(pageKey)}`,
        this.quiet
      )
      .pipe(catchError(this.handleError));
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /** Normalize ApiService/axios errors to the `{status?, message}` the page renders. */
  private handleError = (error: any): Observable<never> => {
    const message =
      error?.message ||
      error?.error ||
      error?.err ||
      error?.detail ||
      'An unexpected error occurred';
    return throwError(() => ({ status: error?.statusCode ?? error?.status, message, raw: error }));
  };
}
